import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { preloadService } from './services/preloadService';
import { useAdmin } from './hooks/useAdmin';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import Demo from './components/pages/Demo';
import Character from './components/pages/Character';
import Create from './components/pages/Create';
import ConversationsPage from './components/pages/Conversations';
import CompanionChat from './components/pages/CompanionChat';
import UserProfile from './components/pages/UserProfile';
import ResetPassword from './components/pages/ResetPassword';
import LoadingTest from './components/LoadingTest';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingScreen from './components/ui/LoadingScreen';

import { suppressResizeObserverErrors } from './utils/errorHandlers';
import './styles/globals.css';

const AppContent: React.FC<{ category?: string }> = ({ category }) => {
  // const { loading } = useAuth(); // Removed unused loading variable

  // Don't block the UI while auth is loading - let Dashboard handle its own loading states
  // if (loading) {
  //   return (
  //     <div style={{ 
  //       display: 'flex', 
  //       justifyContent: 'center', 
  //       alignItems: 'center', 
  //       height: '100vh',
  //       width: '100vw',
  //       margin: 0,
  //       padding: 0,
  //     }}>
  //       <div>Loading...</div>
  //     </div>
  //   );
  // }

  // Always show Dashboard regardless of authentication status
  return <Dashboard category={category} />;
};

// Admin route component with proper API-based admin checking
const AdminRoute: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin, loading, error } = useAdmin();

  // Show loading state while checking admin status
  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <LoadingScreen
          title="Checking Access"
          message="Verifying admin privileges..."
          variant="inline"
        />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Admin Access Required</h1>
          <p className="text-dark-300 mb-6">Please sign in with an admin account to access the admin dashboard.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors"
          >
            Go to Main App
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Error Checking Admin Status</h1>
          <p className="text-dark-300 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors mr-4"
          >
            Retry
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Go to Main App
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-dark-300 mb-6">You don't have admin privileges to access this dashboard.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors"
          >
            Go to Main App
          </button>
        </div>
      </div>
    );
  }

  return <AdminDashboard />;
};

const App: React.FC = () => {
  // Suppress ResizeObserver errors globally
  useEffect(() => {
    const cleanup = suppressResizeObserverErrors();
    
    // Preload data based on current URL
    preloadService.preloadFromUrl(window.location.href);
    
    return cleanup;
  }, []);

  return (
    <ErrorBoundary>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <Routes>
            <Route path="/demo" element={<Demo />} />
            <Route path="/admin" element={<AdminRoute />} />
            <Route path="/character" element={<Character />} />
            <Route path="/create" element={<Create />} />
            <Route path="/conversations" element={<ConversationsPage />} />
            <Route path="/conversations/:companionName/:companionId" element={<CompanionChat />} />
            <Route path="/loading-test" element={<LoadingTest />} />
            <Route path="/user/:userId" element={<UserProfile />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* New 2x2 Category Routes */}
            <Route path="/female-realistic" element={<AppContent category="female-realistic" />} />
            <Route path="/female-anime" element={<AppContent category="female-anime" />} />
            <Route path="/male-realistic" element={<AppContent category="male-realistic" />} />
            <Route path="/male-anime" element={<AppContent category="male-anime" />} />

            {/* Legacy Routes for backward compatibility */}
            <Route path="/girls" element={<AppContent category="female-realistic" />} />
            <Route path="/anime" element={<AppContent category="female-anime" />} />
            <Route path="/guys" element={<AppContent category="male-realistic" />} />

            <Route path="/" element={<AppContent />} />
          </Routes>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;